import React, { useEffect, useCallback, useState, useRef, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { RemoveScrollBar } from 'react-remove-scroll-bar'
import clsx from 'clsx'
import { RootState } from '../../redux/store'
import { useDispatch, useSelector } from 'react-redux'
import PointBar from '../../components/userAdmin/PointBar'
import PerceptionBar from '../../components/userAdmin/PerceptionBar'
import { Button, PageTitle, SkipLink } from '../../components/atoms'
import useModalManager from '../../hooks/useModalManager'
import plusOneXPIcon from '../../assets/images/plus-one-xp.png'
import { VideoPlayer } from '../../../src/components'
import apiServices from '../../libs/services'
import { CourseData, PostAnswerData, QuestionData, McqGameInputData } from '../../models/global.types'
import { LoadingSpinner } from '../../components/atoms/'
import { MessageModal, ModuleBackground } from '../../components/molecules'
import { toast } from 'react-toastify'
import { logger, modules } from '../../utils'
import { setPlayerButtonsDisabled } from '../../redux/slices/playerSlice'
import { setCurrentAttempt } from '../../redux/slices/quizSlice'
import { HTTP_STATUS, MetadataType, ModuleName, ModuleNameScreenReader, QuestionType, SfxTrack } from '../../enums/enums'
import MultipleChoiceQuestions from '../../components/organisms/MultipleChoiceQuestions'
import { ButtonTypes } from '../../components/atoms/Button/Button'
import ReskinStudentHeader from '../../components/userAdmin/ReskinStudentHeader'
import {
  DASHBOARD_RESKIN_COURSE_VIDEO_SIZE_CLASS,
  DASHBOARD_RESKIN_HEALTHBAR_VIDEO_GAP_CLASS,
  DASHBOARD_RESKIN_TF_MODAL_CLASS
} from '../../components/userAdmin/dashboardReskinTiles'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import { HighlightEffect } from '../../enums/KeyboardControls'
import Text from '../../components/atoms/Text/Text'
import { getVideoUrl } from '../../utils/video'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { ensureMcqPartMetadata, getMcqPart, getVideoTimestampForEadPreference } from '../../utils/questions'
import { OnProgressProps } from 'react-player/base'
import { useBackgroundMusic } from '../../contexts/BackgroundMusicContext'
import { getBgmUrl, getSfxUrl } from '../../utils/audio'
import { useNetworkErrorHandler } from '../../contexts/NetworkErrorContext'
import PerceptionBarModal from '../../components/molecules/PerceptionBarModal/PerceptionBarModal'
import { cacheAnswer, cacheAllAnswers, getCachedAnswers, clearCachedAnswers } from '../../utils/moduleAnswerCache'

const minimumScoreToPass = 8

export enum FocusableElement {
  ButtonTrue = 'button-true',
  ButtonFalse = 'button-false',
}

export const idCourseMainContent = 'course-main-content'

export const stringInMyOpinion: string = 'In my opinion,'

const UserCourse: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const isPerceptionValueChanged = useRef<boolean>(false)
  const [perceptionValue, setPerceptionValue] = useState<number>(50)
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false)
  const [hasBgmOnVideo, setHasBgmOnVideo] = useState<boolean>(true)
  const [questionModalShouldFocus, setQuestionModalShouldFocus] = useState<boolean>(true)
  const [questionModalVisible, setQuestionModalVisible] = useState<boolean>(false)
  const [failureModalVisible, setFailureModalVisible] = useState<boolean>(false)
  const [videoFinished, setVideoFinished] = useState<boolean>(false)
  const [gameEnded, setGameEnded] = useState<boolean>(false)
  const [experiencePoints, setExperiencePoints] = useState<number>(0)
  const [courseData, setCourseData] = useState<CourseData | null>(null)
  const [currentVideoQuestionIndex, setCurrentVideoQuestionIndex] = useState<number>(0)
  const [isAddingPoint, setIsAddingPoint] = useState<boolean>(false)
  const [plusPoint, setPlusPoint] = useState<number>(0)
  const [level, setLevel] = useState<number>(0)
  const [showCorrectAnswer, setShowCorrectAnswer] = useState<boolean>(false)
  const [activeModule, setActiveModule] = useState<ModuleName | null>(null)
  const [modulePartCount, setModulePartCount] = useState<number>(1)
  const [currentModulePart, setCurrentModulePart] = useState<number | null>(null)
  const [currentPartVideoQuestions, setCurrentPartVideoQuestions] = useState<QuestionData[] | null>(null)
  const [mcqGameData, setMcqGameData] = useState<McqGameInputData | null>(null)
  const [bgModule, setBgModule] = useState<ModuleName | null>(null)
  const [moduleId, setModuleId] = useState<string>('')
  const [displayInstructions, setDisplayInstructions] = useState<boolean>(false)
  const [videoUrl, setVideoUrl] = useState('')
  const [captionsUrl, setCaptionsUrl] = useState('')
  const [tfAnswerButtonsActive, setTfAnswerButtonsActive] = useState<boolean>(true)
  const [userGotIncorrectTFAnswer, setUserGotIncorrectTFAnswer] = useState<boolean>(false)
  const [videoAnswers, setVideoAnswers] = useState<PostAnswerData[]>([])
  const [mcqAnswers, setMcqAnswers] = useState<PostAnswerData[]>([])
  const [videoPlayerKey, setVideoPlayerKey] = useState<number>(0)
  const [mcqGameKey, setMcqGameKey] = useState<number>(0)
  // Tracks XP at the moment MCQ game starts (preserves video T/F gains when reloading MCQ)
  const [mcqStartXp, setMcqStartXp] = useState<number>(0)
  const [completionSaving, setCompletionSaving] = useState<boolean>(false)
  const experiencePointsRef = useRef<number>(0)
  // Tracks the question index for which the appear SFX has already been played, so rapid ReactPlayer onProgress re-fires (before the pause settles) don't double-play the sound.
  const sfxAppearFiredForIndexRef = useRef<number>(-1)
  const { firstActiveModule, modules: reduxModules } = useSelector((state: RootState) => state.module)
  const { institution } = useSelector((state: RootState) => state.institution)

  // Keep the ref updated with current experience points
  useEffect(() => {
    experiencePointsRef.current = experiencePoints
  }, [experiencePoints])

  const networkErrorHandler = useNetworkErrorHandler()
  const keyboardControls = useKeyboardControls()
  const bgMusic = useBackgroundMusic()
  const userPreferences = useUserPreferences()

  const saveAnswersErrorMsg = 'There was a problem saving your answers.  Please try again and, if the problem persists, reach out to DopaGE support.'

  const query = new URLSearchParams(useLocation().search)
  const a11yParam = query.get('a11y') === 'true'

  const [mcqInstructionsVideoUrl] = getVideoUrl('mcq-instructions', null, false)

  // T/F question sound effects (DMVP-1023).
  const sfxQuestionAppearUrl = getSfxUrl(SfxTrack.QuestionAppear)
  const sfxQuestionCorrectUrl = getSfxUrl(SfxTrack.QuestionCorrect)
  const sfxQuestionIncorrectUrl = getSfxUrl(SfxTrack.QuestionIncorrect)

  const { isModalVisible, openModal, closeModal } = useModalManager(false)

  const moduleNameScreenReader = modules.getModuleNameScreenReader(activeModule)
  const moduleNamePerceptionBar = modules.getModuleNamePerceptionBar(activeModule)
  const pointBarRef = useRef<HTMLDivElement>(null)

  // Manage BG music
  useEffect(() => {
    if (!activeModule) { return }
    if (!videoFinished &&
        !hasBgmOnVideo &&
        !isModalVisible &&
        !failureModalVisible &&
        activeModule !== ModuleName.Lightning) {
      return
    }

    const url = getBgmUrl(activeModule)
    bgMusic.play(url)
    return () => {
      bgMusic.pause()
    }
  }, [bgMusic, activeModule, videoFinished, isModalVisible, failureModalVisible, hasBgmOnVideo])

  // Reload video with current preferences without restarting module
  const reloadVideo = useCallback(() => {
    if (activeModule === null || videoFinished) return

    const wasPlaying = isVideoPlaying
    setIsVideoPlaying(false)

    // Force video player to remount by changing the key
    setVideoPlayerKey(prev => prev + 1)

    // Update video URLs based on EAD preference
    setTimeout(() => {
      if (displayInstructions) {
        const [newVideoUrl, newCaptionsUrl] = getVideoUrl('mcq-instructions', null, false)
        setVideoUrl(newVideoUrl)
        setCaptionsUrl(newCaptionsUrl)
      } else {
        const [newVideoUrl, newCaptionsUrl] = getVideoUrl(activeModule, currentModulePart, userPreferences.preferenceA11yEad || a11yParam)
        setVideoUrl(newVideoUrl)
        setCaptionsUrl(newCaptionsUrl)
      }

      // Resume playing if video was playing before reload
      if (wasPlaying && !questionModalVisible) {
        setTimeout(() => {
          setIsVideoPlaying(true)
        }, 200)
      }
    }, 100)
  }, [activeModule, currentModulePart, userPreferences.preferenceA11yEad, a11yParam, displayInstructions, isVideoPlaying, questionModalVisible, videoFinished])

  // Listen for EAD preference changes and reload video
  const previousEadPreference = useRef<boolean>(userPreferences.preferenceA11yEad)
  useEffect(() => {
    if (previousEadPreference.current !== userPreferences.preferenceA11yEad && activeModule !== null && !videoFinished) {
      reloadVideo()
    }
    previousEadPreference.current = userPreferences.preferenceA11yEad
  }, [userPreferences.preferenceA11yEad, activeModule, videoFinished, reloadVideo])

  // Reload MCQ game with current preferences without restarting module
  const reloadMcqGame = useCallback(() => {
    if (activeModule === null || !videoFinished || gameEnded) return

    // Force MCQ component to remount by changing the key
    setMcqGameKey(prev => prev + 1)

    // Reset XP back to the value when MCQ game started (preserves video T/F gains)
    setExperiencePoints(mcqStartXp)

    // Clear any in-progress MCQ state
    // Note: Internal MCQ component state will be reset by remounting
    // MCQ answers are preserved as they represent completed questions
  }, [activeModule, videoFinished, gameEnded, mcqStartXp])

  // Listen for MCQ accessibility preference changes and reload MCQ game
  const previousMcqAccessibilityPreference = useRef<boolean>(userPreferences.preferenceA11yMcq)
  useEffect(() => {
    if (previousMcqAccessibilityPreference.current !== userPreferences.preferenceA11yMcq &&
        activeModule !== null &&
        videoFinished &&
        !gameEnded) {
      reloadMcqGame()
    }
    previousMcqAccessibilityPreference.current = userPreferences.preferenceA11yMcq
  }, [userPreferences.preferenceA11yMcq, activeModule, videoFinished, gameEnded, reloadMcqGame])

  /**
   * Resets state to the beginning of the module.
   * For use when user does not pass the module - will
   * set them back to the beginning.
   */
  const resetModule = useCallback((): void => {
    setFailureModalVisible(true)
    setCurrentVideoQuestionIndex(0)
    setCurrentModulePart(1)
    setVideoFinished(false)
    setHasBgmOnVideo(true)
    setMcqAnswers([])
    setVideoAnswers([])
    setExperiencePoints(courseData?.totalScore ?? 0)
    setMcqStartXp(courseData?.totalScore ?? 0)
    setIsVideoPlaying(false)
  }, [courseData])

  const fetchQuestions = useCallback(async (): Promise<void> => {
    try {
      const res = await apiServices.fetchCurrentAttempt()
      const attemptId: string = res?.data?.data?.attempt?.id
      const score: number = res?.data?.data?.attempt?.totalScore

      if (attemptId !== '') {
        const currentSessionRes = await apiServices.getCurrentSession(attemptId)
        const sessionId = currentSessionRes?.data?.data?.session?.id
        setModuleId(currentSessionRes?.data?.data?.session?.moduleId)
        const totalScore: number = score !== null ? score : 0
        const moduleName = modules.parseModuleName(currentSessionRes?.data?.data?.session?.name?.toLowerCase())
        const currentLevel = modules.getModuleLevel(moduleName, reduxModules)
        const partCount = parseInt(currentSessionRes?.data?.data?.session?.partCount)
        setModulePartCount(partCount)
        setCurrentModulePart(1)
        setLevel(currentLevel)

        const questionsRes = await apiServices.fetchQuestions(attemptId, sessionId)
        const questions: QuestionData[] = questionsRes?.data?.data?.questions
        let videoQuestions = new Array<QuestionData>()
        let mcqQuestions = new Array<QuestionData>()

        if (questions !== undefined && questions !== null && questions.length > 0) {
          // Filter video questions
          videoQuestions = questions
            .filter(q => q.type === QuestionType.Video)
            .sort((q1, q2) => {
              const videoMeta1 = q1.metadata.find(m => m.name === MetadataType.QuestionVideoTimestamp)
              const videoMeta2 = q2.metadata.find(m => m.name === MetadataType.QuestionVideoTimestamp)
              if (videoMeta1 === undefined && videoMeta2 === undefined) {
                return 0
              } else if (videoMeta1 === undefined) {
                return -1
              } else if (videoMeta2 === undefined) {
                return 1
              }
              return parseFloat(videoMeta1.value) - parseFloat(videoMeta2.value)
            })

          // Filter multiple choice questions
          mcqQuestions = questions
            .filter(q => q.type === QuestionType.Mcq)
        }

        for (const question of mcqQuestions) {
          ensureMcqPartMetadata(question)
        }

        for (const question of videoQuestions) {
          ensureMcqPartMetadata(question)

          // Randomize True/False question answer order
          question.tfAnswerOrder = Math.floor(Math.random() * 2)
        }

        setActiveModule(moduleName)
        setBgModule(moduleName)

        setCourseData({
          levelName: moduleName as string,
          totalScore,
          sessionId,
          attemptId,
          videoQuestions,
          mcqQuestions
        })
        setExperiencePoints(totalScore)
        setMcqStartXp(totalScore)

        // DMVP-1020: Recover cached answers from a previous failed completion
        const cached = getCachedAnswers()
        if (cached != null && cached.sessionId === sessionId) {
          logger.log('Module Recovery: Found cached answers, re-submitting', {
            sessionId, answerCount: cached.answers.length
          })

          const recoverAnswers = async (): Promise<void> => {
            await apiServices.postSessionAnswers(attemptId, sessionId, cached.answers, false, true)
            await apiServices.completeSession(attemptId, sessionId, true)
            if (moduleName === ModuleName.Lightning) {
              const updatedAttempt = await (await apiServices.completeAttempt(attemptId, true)).data.attempt
              dispatch(setCurrentAttempt(updatedAttempt))
              clearCachedAnswers()
              if (institution?.displaySurvey === true) {
                navigate('/user-dashboard/congrats')
              } else {
                navigate('/user-dashboard/completed')
              }
            } else {
              await apiServices.startNextSession(attemptId, true)
              clearCachedAnswers()
              window.location.reload()
            }
          }

          try {
            await recoverAnswers()
            return
          } catch (err) {
            logger.error('Module Recovery: Failed to re-submit cached answers, reloading', { error: (err as Error).message })
            window.location.reload()
            return
          }
        } else {
          clearCachedAnswers()
        }
      } else {
        localStorage.clear()
      }
    } catch (err) {
      logger.error((err as Error))
    }
  }, [reduxModules, dispatch, institution?.displaySurvey, navigate])

  // Select current part questions
  useEffect(() => {
    if (courseData === null) { return }

    let mcqQuestions: QuestionData[]
    let videoQuestions: QuestionData[]

    if (activeModule === ModuleName.Lightning) {
      mcqQuestions = courseData?.mcqQuestions
      videoQuestions = courseData?.videoQuestions
    } else {
      mcqQuestions = courseData?.mcqQuestions.filter((qd) => getMcqPart(qd) === currentModulePart)
      videoQuestions = courseData?.videoQuestions.filter((qd) => getMcqPart(qd) === currentModulePart)
    }

    setCurrentPartVideoQuestions(videoQuestions)

    // Object to pass to the MCQ game component
    setMcqGameData({
      levelName: courseData.levelName,
      totalScore: courseData.totalScore,
      questions: mcqQuestions
    })
  }, [currentModulePart, courseData, activeModule])

  useEffect(() => {
    void fetchQuestions()
  }, [fetchQuestions])

  useEffect(() => {
    if (activeModule !== ModuleName.Lightning) {
      openModal()
    } else {
      closeModal()
      setVideoFinished(true)
      // For Lightning module, capture current XP as MCQ starting point
      setMcqStartXp(experiencePointsRef.current)
    }
  }, [activeModule, openModal, closeModal])

  useEffect(() => {
    if (displayInstructions) {
      setVideoUrl('')
      setTimeout(() => {
        const [newVideoUrl, newCaptionsUrl] = getVideoUrl('mcq-instructions', null, false)
        setVideoUrl(newVideoUrl)
        setCaptionsUrl(newCaptionsUrl)
      }, 2000)
    } else {
      if (activeModule === null) {
        setVideoUrl('')
        setCaptionsUrl('')
      } else {
        const [newVideoUrl, newCaptionsUrl] = getVideoUrl(activeModule, currentModulePart, userPreferences.preferenceA11yEad || a11yParam)
        setVideoUrl(newVideoUrl)
        setCaptionsUrl(newCaptionsUrl)
      }
    }
  }, [displayInstructions, activeModule, userPreferences.preferenceA11yEad, a11yParam, currentModulePart])

  useEffect(() => {
    if (questionModalVisible) {
      dispatch(setPlayerButtonsDisabled(true))
    } else {
      dispatch(setPlayerButtonsDisabled(false))
    }
  }, [dispatch, questionModalVisible])

  const handleGoClick = useCallback(() => {
    const managePerceptionBar = async (): Promise<void> => {
      if (gameEnded) {
        await apiServices.createUserPerception(moduleId, 'post', perceptionValue)
        navigate('/user-dashboard')
      } else {
        await apiServices.createUserPerception(moduleId, 'pre', perceptionValue)
        closeModal()
      }
    }
    void managePerceptionBar()
  }, [gameEnded, closeModal, navigate, perceptionValue, moduleId])

  const handlePerceptionChange = useCallback((value: number) => {
    if (!isPerceptionValueChanged?.current) {
      isPerceptionValueChanged.current = true
    }
    setPerceptionValue(value)
  }, [])

  const handleResetClick = useCallback(() => setPerceptionValue(50), [])

  const handleVideoPause = useCallback(() => {
    setIsVideoPlaying(false)
  }, [])

  const handleVideoPlay = useCallback(() => {
    setIsVideoPlaying(true)
    setHasBgmOnVideo(false)
  }, [])

  const handleVideoProgress = useCallback((state: OnProgressProps): void => {
    const { playedSeconds } = state

    if (currentPartVideoQuestions === null) { return }

    const question = currentPartVideoQuestions[currentVideoQuestionIndex]
    const timestamp = getVideoTimestampForEadPreference(question, userPreferences.preferenceA11yEad || a11yParam)

    if (timestamp === null) { return }

    if (playedSeconds >= timestamp) {
      setIsVideoPlaying(false)
      setQuestionModalVisible(true)
      if (sfxAppearFiredForIndexRef.current !== currentVideoQuestionIndex) {
        sfxAppearFiredForIndexRef.current = currentVideoQuestionIndex
        bgMusic.playSoundFx(sfxQuestionAppearUrl)
      }
    }
  }, [currentPartVideoQuestions, currentVideoQuestionIndex, userPreferences.preferenceA11yEad, a11yParam, bgMusic, sfxQuestionAppearUrl])

  const handleTFQuestionClick = useCallback(async (answer: boolean): Promise<void> => {
    if (currentPartVideoQuestions === null) {
      const error = new Error('Question data for current course part is not available')
      throw (error)
    }

    const question = currentPartVideoQuestions[currentVideoQuestionIndex]

    setTfAnswerButtonsActive(false)

    // Get the correct answer
    const correctAnswer = question.answers.find((ans) => ans.is_correct)

    if (correctAnswer !== null && correctAnswer !== undefined && correctAnswer.text === answer.toString()) {
      // Reveal correct answer to the user
      setShowCorrectAnswer(true)

      bgMusic.playSoundFx(sfxQuestionCorrectUrl)

      // Add answer data to state and show correct answer UX
      setVideoAnswers([
        ...videoAnswers,
        {
          question_id: question.id,
          answers: [correctAnswer.id]
        }
      ])

      setExperiencePoints(experiencePoints + 1)
      setPlusPoint(1)
      setIsAddingPoint(true)

      setTimeout(() => {
        setIsAddingPoint(false)
      }, 2000)
    } else {
      // Show incorrect answer UX
      setUserGotIncorrectTFAnswer(true)

      bgMusic.playSoundFx(sfxQuestionIncorrectUrl)

      // Reveal the correct answer after 1 second
      setTimeout(() => {
        setShowCorrectAnswer(true)
      }, 1000)
    }

    // Hide T/F questions and resume video playing
    setTimeout(() => {
      setTfAnswerButtonsActive(true)
      setShowCorrectAnswer(false)
      setUserGotIncorrectTFAnswer(false)
      setCurrentVideoQuestionIndex(currentVideoQuestionIndex + 1)
      setQuestionModalVisible(false)
      setQuestionModalShouldFocus(true)
      dispatch(setPlayerButtonsDisabled(false))
      setIsVideoPlaying(true)
    }, 2000)
  }, [currentPartVideoQuestions, currentVideoQuestionIndex, dispatch, experiencePoints, videoAnswers, bgMusic, sfxQuestionCorrectUrl, sfxQuestionIncorrectUrl])

  const handleVideoEnded = useCallback((): void => {
    setIsVideoPlaying(false)
    
    logger.addBreadcrumb('Video Phase: Video ended, transitioning to MCQ', {
      module: activeModule,
      experiencePointsState: experiencePoints,
      experiencePointsRef: experiencePointsRef.current,
      currentModulePart,
      videoUrl
    })

    if (activeModule === firstActiveModule.toLowerCase() &&
        videoUrl !== mcqInstructionsVideoUrl &&
        currentModulePart === 1) {
      setDisplayInstructions(true)
    } else {
      setVideoFinished(true)
      setDisplayInstructions(false)
      setCurrentVideoQuestionIndex(0)
      // Capture current XP (including video T/F gains) as MCQ starting point
      const mcqStartValue = experiencePointsRef.current
      setMcqStartXp(mcqStartValue)
      
      logger.addBreadcrumb('MCQ Phase: Starting', {
        mcqStartXp: mcqStartValue,
        experiencePointsState: experiencePoints,
        stateDrift: Math.abs(experiencePoints - mcqStartValue)
      })
    }
  }, [
    firstActiveModule, mcqInstructionsVideoUrl, videoUrl, setDisplayInstructions, setVideoFinished,
    activeModule, currentModulePart, experiencePoints
  ])

  const handleEndGame = useCallback(async (mcqPostAnswerData: PostAnswerData[]): Promise<void> => {
    try {
      if (courseData === null) {
        const error = new Error('Course data is not available')
        throw (error)
      }

      if (currentModulePart === null) {
        const error = new Error('Current module part is not set')
        throw (error)
      }

      // Merge T/F, new MCQ and existing MCQ
      const allAnswers: PostAnswerData[] = [...mcqPostAnswerData, ...mcqAnswers, ...videoAnswers]
      const { attemptId, sessionId, totalScore } = courseData
      // FIX: Use ref instead of state to avoid race condition
      const newExperiencePoints = experiencePointsRef.current - totalScore
      
      // Comprehensive Sentry logging for XP calculation
      const xpContext = {
        module: activeModule,
        attemptId,
        sessionId,
        experiencePointsState: experiencePoints,
        experiencePointsRef: experiencePointsRef.current,
        totalScore,
        newExperiencePoints,
        calculationUsed: 'ref', // Using ref to avoid race condition
        minimumScoreToPass,
        willPass: newExperiencePoints >= minimumScoreToPass,
        mcqStartXp,
        currentModulePart,
        modulePartCount,
        allAnswersCount: allAnswers.length,
        videoAnswersCount: videoAnswers.length,
        mcqAnswersCount: mcqAnswers.length + mcqPostAnswerData.length,
        stateDriftDetected: Math.abs(experiencePoints - experiencePointsRef.current) > 0
      }
      
      logger.setContext('Module-Completion-XP-Calculation', xpContext)
      logger.addBreadcrumb('Module Completion: Starting', xpContext)
      
      // CRITICAL: Log if state and ref diverge
      if (experiencePoints !== experiencePointsRef.current) {
        logger.error('Module Completion: State/Ref XP mismatch detected!', xpContext)
      }

      if (currentModulePart < modulePartCount) {
        // Advance to next module part
        setMcqAnswers([
          ...mcqAnswers,
          ...mcqPostAnswerData
        ])
        setCurrentModulePart(currentModulePart + 1)
        setVideoFinished(false)
      } else {
        // Last module part was completed.
        // Need to make synchronous API calls so that completeSession calculates the correct score.
        if (activeModule !== ModuleName.Lightning) {
          if (newExperiencePoints < minimumScoreToPass) {
            logger.log('Module Completion: FAILED - resetting module', {
              module: activeModule,
              newExperiencePoints,
              minimumScoreToPass,
              deficit: minimumScoreToPass - newExperiencePoints,
              experiencePointsState: experiencePoints,
              experiencePointsRef: experiencePointsRef.current,
              shouldHavePassed: (experiencePointsRef.current - totalScore) >= minimumScoreToPass
            })
            resetModule()
          } else {
            logger.addBreadcrumb('Module Completion: PASSED - posting answers', {
              newExperiencePoints,
              minimumScoreToPass,
              surplus: newExperiencePoints - minimumScoreToPass
            })

            // Cache answers before API calls for crash resilience (DMVP-1020)
            cacheAllAnswers(sessionId, attemptId, allAnswers)
            setCompletionSaving(true)

            try {
              const response = await apiServices.postSessionAnswers(attemptId, sessionId, allAnswers, false, true)

              logger.addBreadcrumb('Module Completion: postSessionAnswers response', {
                status: response?.request?.status,
                statusText: response?.request?.statusText
              })

              if (response?.request?.status !== HTTP_STATUS.CREATED) {
                throw new Error('Failed to post session answers')
              }

              logger.addBreadcrumb('Module Completion: Calling completeSession')
              await apiServices.completeSession(attemptId, sessionId, true)

              logger.log('Module Completion: Session completed successfully', {
                module: activeModule, sessionId, attemptId
              })

              setGameEnded(true)

              if (isPerceptionValueChanged?.current) {
                isPerceptionValueChanged.current = false
              }
              await apiServices.startNextSession(attemptId, true)
              clearCachedAnswers()
              setCompletionSaving(false)
              setPerceptionValue(50)
              openModal()
            } catch (err: any) {
              logger.error('Module Completion: Module completion failed, reloading for recovery', {
                errorMessage: err.message, attemptId, sessionId
              })
              networkErrorHandler.setHasTimeoutError(false)
              window.location.reload()
            }
          }
        } else {
          // Lighting module was completed
          logger.addBreadcrumb('Module Completion: Lightning module complete')

          // Cache answers before API calls for crash resilience (DMVP-1020)
          cacheAllAnswers(sessionId, attemptId, allAnswers)
          setCompletionSaving(true)

          try {
            const response = await apiServices.postSessionAnswers(attemptId, sessionId, allAnswers, false, true)
            if (response?.request?.status !== HTTP_STATUS.CREATED) {
              throw new Error('Failed to save answers')
            }
            await apiServices.completeSession(attemptId, sessionId, true)
            setGameEnded(true)
            const updatedAttempt = await (await apiServices.completeAttempt(attemptId, true)).data.attempt
            dispatch(setCurrentAttempt(updatedAttempt))
            clearCachedAnswers()
            setCompletionSaving(false)
            if (institution?.displaySurvey === true) {
              navigate('/user-dashboard/congrats')
            } else {
              navigate('/user-dashboard/completed')
            }
          } catch (err: any) {
            logger.error('Module Completion: Lightning completion failed, reloading for recovery', {
              errorMessage: err.message, attemptId, sessionId
            })
            networkErrorHandler.setHasTimeoutError(false)
            window.location.reload()
          }
        }
      }
    } catch (error: any) {
      logger.error('Module Completion: Error in handleEndGame', {
        errorMessage: error.message,
        errorStack: error.stack,
        module: activeModule,
        experiencePoints,
        experiencePointsRef: experiencePointsRef.current
      })
      toast.error(error.message)
      logger.error(error)
    }
  }, [
    activeModule, videoAnswers, courseData, dispatch, experiencePoints, isPerceptionValueChanged, navigate,
    openModal, resetModule, setGameEnded, setPerceptionValue, currentModulePart, modulePartCount, mcqAnswers, mcqStartXp,
    institution, networkErrorHandler
  ])

  const handleUpdateXP = useCallback((mcqAnswerData: PostAnswerData[]): void => {
    if (courseData === null) {
      const error = new Error('Course data is not available')
      throw (error)
    }

    // Log XP update with current state values
    const beforeUpdate = experiencePoints
    const beforeUpdateRef = experiencePointsRef.current
    
    logger.addBreadcrumb('XP Update: handleUpdateXP called', {
      module: activeModule,
      beforeUpdate,
      beforeUpdateRef,
      expectedAfterUpdate: beforeUpdate + 3,
      questionCount: mcqAnswerData.length,
      sessionId: courseData.sessionId
    })

    setExperiencePoints((prevPoints) => {
      const newPoints = prevPoints + 3
      
      logger.addBreadcrumb('XP Update: setState callback executed', {
        prevPoints,
        newPoints,
        refValue: experiencePointsRef.current
      })
      
      return newPoints
    })
    setPlusPoint(3)
    setIsAddingPoint(true)
    if (activeModule === ModuleName.Lightning) {
      const answerData: PostAnswerData[] = mcqAnswerData
      const { attemptId, sessionId } = courseData

      // Cache each answer for crash resilience (DMVP-1020)
      for (const answer of answerData) {
        cacheAnswer(sessionId, attemptId, answer)
      }

      // Eagerly save intermediate Lightning answers
      const saveAnswers = async (): Promise<void> => {
        const response = await apiServices.postSessionAnswers(attemptId, sessionId, answerData, true)
        if (response?.request?.status !== HTTP_STATUS.CREATED) {
          toast.error(saveAnswersErrorMsg, { position: toast.POSITION.TOP_CENTER })
          logger.error('Failed to save Lightning answers', {
            attemptId,
            sessionId,
            status: response?.request?.status
          })
        }
      }

      void saveAnswers()
    }
    setTimeout(() => {
      setIsAddingPoint(false)
    }, 2000)
  }, [activeModule, courseData, setExperiencePoints, setPlusPoint, setIsAddingPoint, experiencePoints])

  const handleNewLightningModule = useCallback((moduleName: string) => {
    setBgModule(modules.parseModuleName(moduleName))
  }, [setBgModule])

  const closeFailureModal: () => void = () => {
    setIsVideoPlaying(true)
    setFailureModalVisible(false)
  }

  // Focus left button by default in True/False questions
  useEffect(() => {
    if (questionModalVisible && questionModalShouldFocus && currentPartVideoQuestions !== null) {
      const question = currentPartVideoQuestions[currentVideoQuestionIndex]

      const trueFirst = question.tfAnswerOrder === 0
      const leftButtonId = trueFirst ? FocusableElement.ButtonTrue : FocusableElement.ButtonFalse

      if (keyboardControls.isEnabled) {
        keyboardControls.setFocusedElement(leftButtonId)
      }
      setQuestionModalShouldFocus(false)
    }
  }, [questionModalVisible, questionModalShouldFocus, keyboardControls, currentVideoQuestionIndex, currentPartVideoQuestions])

  const responsiveHeading = 'text-[3vh] w-[60%] lg:w-full lg:text-[4vh]'
  const responsiveButton = 'text-[6vh] w-[27vh] h-[16vh] focus-visible:outline-none'
  const responsiveModal = DASHBOARD_RESKIN_TF_MODAL_CLASS
  const responsiveUnity = 'h-[90vh]'

  const renderMcqH1 = useCallback((activeModule: ModuleName | null, moduleNameScreenReader: ModuleNameScreenReader | null): ReactNode => {
    let stringHeader = `${moduleNameScreenReader as string} Level - Quiz Questions`

    if (activeModule === ModuleName.Lightning) {
      stringHeader = 'Lightning Round Quiz'
    }

    return (
      <PageTitle title={stringHeader} />
    )
  }, [])

  const renderTrueFalseQuestions = useCallback((): JSX.Element | null => {
    if (currentPartVideoQuestions === null) { return null }

    const question = currentPartVideoQuestions[currentVideoQuestionIndex]
    const trueFirst = question.tfAnswerOrder === 0

    const buttonTrueClass = clsx(responsiveButton,
      'btn-option text-white text-center font-bold cursor-pointer',
              `btn-opt-true-${level} ${trueFirst ? 'order-1' : 'order-2'}`,
              `${showCorrectAnswer ? `btn-opted-true-${level}` : `btn-opt-true-${level}`}`
    )
    const buttonFalseClass = clsx(responsiveButton,
      'btn-option btn-opt-false text-white text-center responsive-button font-bold cursor-pointer',
              `${trueFirst ? 'order-2' : 'order-1'}`,
              `${userGotIncorrectTFAnswer ? 'btn-opted-false' : `btn-opt-false-${level}`}`
    )

    const button1Id = trueFirst ? FocusableElement.ButtonTrue : FocusableElement.ButtonFalse
    const button2Id = trueFirst ? FocusableElement.ButtonFalse : FocusableElement.ButtonTrue
    const button1Class = trueFirst ? buttonTrueClass : buttonFalseClass
    const button2Class = trueFirst ? buttonFalseClass : buttonTrueClass
    const button1AriaLabel = trueFirst ? 'True option' : 'False option'
    const button2AriaLabel = trueFirst ? 'False option' : 'True option'

    return (
      <span role='complementary'>
        <KeyboardControl>
          <button
            id={button1Id}
            className={button1Class}
            aria-label={button1AriaLabel}
            disabled={!tfAnswerButtonsActive}
            onClick={() => { void handleTFQuestionClick(trueFirst) }}
          />
        </KeyboardControl>
        <KeyboardControl>
          <button
            id={button2Id}
            className={button2Class}
            aria-label={button2AriaLabel}
            disabled={!tfAnswerButtonsActive}
            onClick={() => { void handleTFQuestionClick(!trueFirst) }}
          />
        </KeyboardControl>
      </span>
    )
  }, [
    currentPartVideoQuestions, currentVideoQuestionIndex, handleTFQuestionClick, level,
    showCorrectAnswer, tfAnswerButtonsActive, userGotIncorrectTFAnswer
  ])

  const renderVideo = useCallback((autoplay: boolean): ReactNode => {
    return (
      <>
        <PageTitle title={`${moduleNameScreenReader ?? ''} Level - Video Lesson`} />
        <VideoPlayer
          key={videoPlayerKey}
          handleEnded={handleVideoEnded}
          handlePlay={handleVideoPlay}
          handlePause={handleVideoPause}
          handleProgress={handleVideoProgress}
          isPlaying={autoplay ? true : isVideoPlaying}
          instructionMode={videoUrl === mcqInstructionsVideoUrl}
          subTitlesUrl={captionsUrl}
          url={videoUrl}
          muted={autoplay}
        />
        {questionModalVisible && currentPartVideoQuestions !== null && (
          <div
            data-testid='course-tf-question-modal'
            className={clsx(responsiveModal, 'absolute zIndex-[100] bg-[rgba(223,248,255,0.9)] rounded-[10px] text-center flex items-center flex-col justify-center')}
          >
            <h2 className={clsx(responsiveHeading, 'p-5 font-bold text-[#030303]')}>
              {currentPartVideoQuestions[currentVideoQuestionIndex].text}
            </h2>
            <div className='mt-1 flex flex-wrap justify-center relative'>
              {renderTrueFalseQuestions()}
              <div
                className={clsx('absolute w-[10vh] zoomer z-10', {
                  hidden: !isAddingPoint || userPreferences.preferenceA11yReduceMotion,
                  visible: isAddingPoint && !userPreferences.preferenceA11yReduceMotion
                })}
              >
                <img src={plusOneXPIcon} alt='plus one' />
              </div>
            </div>
          </div>
        )}
      </>
    )
  }, [
    captionsUrl, currentVideoQuestionIndex, currentPartVideoQuestions, handleVideoProgress, handleVideoEnded,
    isAddingPoint, isVideoPlaying, mcqInstructionsVideoUrl, moduleNameScreenReader, questionModalVisible,
    renderTrueFalseQuestions, responsiveModal, videoUrl, handleVideoPause, handleVideoPlay, videoPlayerKey, userPreferences.preferenceA11yReduceMotion
  ])

  const renderFailureModal = useCallback((): ReactNode => (
    <MessageModal
      id='failure-modal'
      onCtaClick={closeFailureModal}
      ctaText='CONTINUE'
      onRequestClose={closeModal}
      isOpen={failureModalVisible}
    >
      <p aria-label='Not enough XP' className='font-primary lg:text-3xl md:text-2xl text-base font-black lg:leading-[2.5rem]'>You did not collect enough XP to complete this Level.&nbsp;&nbsp;<br className='sm:hidden' />
        Now, you have another chance to restart it.&nbsp;&nbsp;<br className='xs:hidden sm:hidden' />
        Collect as many XP as you can to level up!
      </p>
    </MessageModal>
  ), [closeModal, failureModalVisible])

  const renderInstructions = useCallback((): ReactNode => (
    <div
      role='button'
      aria-label='Instruction video'
      tabIndex={0}
      onContextMenu={e => e.preventDefault()}
      className='flex items-center flex-col h-[90%]'
    >
      {
        renderVideo(true)
      }
    </div>
  ), [renderVideo])

  const isResetActive =
    keyboardControls.isEnabled && keyboardControls.isElementFocused('reset-button')
  const isGoActive =
    keyboardControls.isEnabled && keyboardControls.isElementFocused('go-button')

  const renderPerceptionModal = useCallback((): ReactNode => {
    if (activeModule === null) {
      return null
    }

    return (
      <div className={clsx(responsiveUnity, 'flex justify-center')}>
        <PerceptionBarModal
          isOpen={isModalVisible}
          onClose={closeModal}
          className='perception-modal'
          initialFocusId='perception-slider'
          allowNestedModals
        >
          <ModuleBackground
            moduleName={bgModule}
            className='p-8'
            data-testid='background-course-perception-modal'
          >
            <SkipLink targetId={idCourseMainContent} />
            <header className='overflow-visible'>
              <ReskinStudentHeader menuClassName='font-primary'>
                <PointBar
                  totalExperiencePoints={experiencePoints}
                  isAddingPoint={isAddingPoint}
                  plusPoint={plusPoint}
                />
              </ReskinStudentHeader>
            </header>
            <span id={`${idCourseMainContent}`} role='main' tabIndex={-1}>
              <PageTitle title={`${moduleNameScreenReader ?? ''} Level - Asking Your Opinion`} />
              <div className='flex flex-col justify-center items-center w-full relative min-h-[calc(90vh-200px)]'>
                <div className='max-w-[1130px] w-full flex justify-center items-center flex-col pb-[100px]'>
                  <div className='w-full'>
                    <Text bold size='2XL' text={stringInMyOpinion} className='text-shadow-white italic' id='perception-question-part1' />
                  </div>
                  <div className='mt-[23px]'>
                    <Text type='paragraph' bold size='2XL' text={`${moduleNamePerceptionBar as string} is`} className='text-shadow-white text-capitalize-first' id='perception-question-part2' />
                  </div>
                  <div className='w-full mt-[40px]'>
                    <PerceptionBar onChange={handlePerceptionChange} value={perceptionValue} questionId='perception-question-part1 perception-question-part2' />
                  </div>
                </div>
                {isPerceptionValueChanged.current
                  ? (
                    <div className='flex justify-center absolute bottom-[40px]'>
                      <div className='flex space-x-5' role='toolbar'>
                        <ControlHighlight
                          active={isResetActive}
                          highlightEffect={HighlightEffect.ButtonHexOutline}
                        >
                          <KeyboardControl>
                            <Button
                              id='reset-button'
                              tabIndex={-1}
                              buttonType={ButtonTypes.secondary}
                              text='RESET'
                              onClick={handleResetClick}
                            />
                          </KeyboardControl>
                        </ControlHighlight>
                        <ControlHighlight
                          active={isGoActive}
                          highlightEffect={HighlightEffect.ButtonHexOutline}
                        >
                          <KeyboardControl>
                            <Button
                              id='go-button'
                              tabIndex={-1}
                              className='diamond-btn h-[80px] w-[210px] rounded-lg font-bold'
                              bgColor='bg-dopage-primary'
                              onClick={handleGoClick}
                            >
                              <span className='font-bold text-[26px] text-neutral-900'>GO</span>
                            </Button>
                          </KeyboardControl>
                        </ControlHighlight>
                      </div>
                    </div>
                    )
                  : null}
              </div>
            </span>
            <footer />
          </ModuleBackground>
        </PerceptionBarModal>
      </div>
    )
  }, [
    activeModule, bgModule, closeModal, experiencePoints, handleGoClick, handlePerceptionChange,
    handleResetClick, isAddingPoint, isGoActive, isModalVisible, isResetActive, moduleNamePerceptionBar,
    perceptionValue, moduleNameScreenReader, plusPoint
  ])

  // Handle error explicitly because the way we render the video may draw it over the network error message
  if (networkErrorHandler.hasTimeoutError && !completionSaving) {
    return null
  }

  // DMVP-1020: Saving overlay when module completion is in progress
  const savingOverlay = (
    <div
      className={`fixed inset-0 bg-black flex items-center justify-center z-50 transition-opacity duration-300 ${
        completionSaving ? 'bg-opacity-100 opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <LoadingSpinner className='w-[25vmin] min-w-[80px] max-w-[240px]' />
    </div>
  )

  // Actual UserCourse component rendering
  if (activeModule !== null) {
    if (!displayInstructions && isModalVisible && activeModule !== ModuleName.Lightning) {
      return (
        <div>{renderPerceptionModal()}</div>
      )
    }

    return (
      <>
      {savingOverlay}
      <ModuleBackground
        moduleName={bgModule}
        className={videoFinished ? 'p-8' : ''}
        data-testid='background-course'
      >
        <RemoveScrollBar />
        <SkipLink targetId={idCourseMainContent} />
        <header className='overflow-visible'>
          <div className={clsx({ hidden: displayInstructions || isModalVisible }, 'relative z-20 overflow-visible')}>
            <ReskinStudentHeader menuClassName={videoFinished ? undefined : 'pt-4'}>
              <PointBar
                ref={pointBarRef}
                totalExperiencePoints={experiencePoints}
                isAddingPoint={isAddingPoint}
                plusPoint={plusPoint}
              />
            </ReskinStudentHeader>
          </div>
        </header>
        <main id={`${idCourseMainContent}`} tabIndex={-1}>
          {!displayInstructions && !isModalVisible && activeModule !== null && activeModule !== ModuleName.Lightning && !videoFinished
            ? (
              <div
                className={clsx('flex items-center flex-col', DASHBOARD_RESKIN_HEALTHBAR_VIDEO_GAP_CLASS)}
                data-testid='course-reskin-video-gap'
              >
                <div
                  role='none'
                  id='video-player'
                  data-testid='video-player'
                  className={clsx('flex items-center flex-col', DASHBOARD_RESKIN_COURSE_VIDEO_SIZE_CLASS)}
                  onContextMenu={e => e.preventDefault()}
                >
                  {renderVideo(false)}
                  {failureModalVisible ? renderFailureModal() : null}
                </div>
              </div>
              )
            : null}
          {displayInstructions && !isModalVisible ? renderInstructions() : null}
          {!displayInstructions && videoFinished && !gameEnded
            ? (
              <div className='relative'>
                {renderMcqH1(activeModule, moduleNameScreenReader)}
                <MultipleChoiceQuestions
                  key={mcqGameKey}
                  trackedComponents={[
                    { id: 'pointBar', ref: pointBarRef }
                  ]}
                  accessibilityView={a11yParam || userPreferences.preferenceA11yMcq}
                  onEndGame={handleEndGame}
                  multipleChoiceQuestionData={mcqGameData}
                  onNewLightningModule={handleNewLightningModule}
                  onUpdateXP={handleUpdateXP}
                />
              </div>
              )
            : null}
        </main>
        <header />
      </ModuleBackground>
      </>
    )
  }

  return (
    <div className='h-screen flex justify-center items-center'><LoadingSpinner className='w-[25vmin] min-w-[80px] max-w-[240px]' /></div>
  )
}

export default UserCourse
